import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Search, Printer, Truck, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEL_CLS = 'px-2 py-1.5 rounded-lg border-2 border-[var(--border-light)] bg-white text-xs font-semibold';

const BASIM = [
  { v: 'bekliyor', tr: 'Bekliyor', en: 'Pending', cls: 'bg-gray-100 text-gray-600' },
  { v: 'basima_gonderildi', tr: 'Basıma Gönderildi', en: 'Sent to print', cls: 'bg-yellow-100 text-yellow-700' },
  { v: 'basildi', tr: 'Basıldı', en: 'Printed', cls: 'bg-green-100 text-green-600' },
];
const KARGO = [
  { v: 'bekliyor', tr: 'Bekliyor', en: 'Pending' },
  { v: 'gonderildi', tr: 'Gönderildi', en: 'Shipped' },
];

export default function AdminCertPrint() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [certs, setCerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [fBasim, setFBasim] = useState('');
  const [fKargo, setFKargo] = useState('');
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [selected, setSelected] = useState([]);
  const [takip, setTakip] = useState({});

  const fetchCerts = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set('search', search);
      if (fBasim) p.set('basim', fBasim);
      if (fKargo) p.set('kargo', fKargo);
      p.set('page', String(page + 1));
      const r = await axios.get(`${API}/admin/issued-certificates?${p.toString()}`, { headers: authHeaders() });
      setCerts(r.data.certificates || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setQuota(r.data.quota || null);
      setSelected([]);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCerts(); }, [search, fBasim, fKargo, page]);

  const setBasim = async (id, durum) => {
    try {
      await axios.put(`${API}/admin/certificates/${id}/basim`, { basim_durumu: durum }, { headers: authHeaders() });
      fetchCerts();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const bulkBasim = async () => {
    if (!selected.length) return;
    try {
      const r = await axios.post(`${API}/admin/certificates/bulk-basim`, { certificate_ids: selected, basim_durumu: 'basima_gonderildi' }, { headers: authHeaders() });
      toast.success(tr(`${r.data.updated} sertifika basıma gönderildi`, `${r.data.updated} sent to print`));
      fetchCerts();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const setKargo = async (id, durum) => {
    try {
      await axios.put(`${API}/admin/certificates/${id}/kargo`, { kargo_durumu: durum, takip_no: takip[id] || '' }, { headers: authHeaders() });
      toast.success(tr('Kargo güncellendi', 'Shipping updated'));
      fetchCerts();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const basimMeta = (v) => BASIM.find((b) => b.v === v) || BASIM[0];

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: tr('Basım & Kargo', 'Print & Shipping') }]}
        title={tr('Sertifika Basım & Kargo', 'Certificate Print & Shipping')}
        subtitle={tr('Basıma gönderme, aylık kota ve kargo takibi', 'Send to print, monthly quota and shipping')}
      />

      {quota && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="card-clay p-3"><p className="text-[10px] font-bold text-[var(--text-muted)]">{tr('Bu Ay (kota)', 'This Month')}</p><p className="text-lg font-bold">{quota.month}</p></div>
          <div className="card-clay p-3"><p className="text-[10px] font-bold text-[var(--text-muted)]">{tr('Basıma Gönderilen', 'Sent to print')}</p><p className="text-lg font-bold text-yellow-600">{quota.basima_gonderildi}</p></div>
          <div className="card-clay p-3"><p className="text-[10px] font-bold text-[var(--text-muted)]">{tr('Basılan', 'Printed')}</p><p className="text-lg font-bold text-green-600">{quota.basildi}</p></div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder={tr('Ara (öğrenci, kurs)...', 'Search...')}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" />
        </div>
        <select value={fBasim} onChange={(e) => { setFBasim(e.target.value); setPage(0); }} className="px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
          <option value="">{tr('Tüm basım', 'All print')}</option>
          {BASIM.map((b) => <option key={b.v} value={b.v}>{lang === 'tr' ? b.tr : b.en}</option>)}
        </select>
        <select value={fKargo} onChange={(e) => { setFKargo(e.target.value); setPage(0); }} className="px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
          <option value="">{tr('Tüm kargo', 'All shipping')}</option>
          {KARGO.map((k) => <option key={k.v} value={k.v}>{lang === 'tr' ? k.tr : k.en}</option>)}
        </select>
        {selected.length > 0 && (
          <button onClick={bulkBasim} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-2 border-[var(--brand-primary)] hover:opacity-90">
            <Printer size={16} /> {tr(`Basıma Gönder (${selected.length})`, `Send to print (${selected.length})`)}
          </button>
        )}
        <span className="text-sm text-[var(--text-muted)] font-semibold">{total}</span>
      </div>

      <div className="card-clay overflow-hidden">
        {loading && <div className="p-3 text-center text-sm text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="text-left p-3 font-bold">{tr('Öğrenci', 'Student')}</th>
                <th className="text-left p-3 font-bold">{tr('Kurs', 'Course')}</th>
                <th className="text-left p-3 font-bold">{tr('Basım', 'Print')}</th>
                <th className="text-left p-3 font-bold">{tr('Adres', 'Address')}</th>
                <th className="text-left p-3 font-bold">{tr('Kargo', 'Shipping')}</th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => {
                const bm = basimMeta(c.basim_durumu);
                const shipped = c.kargo_durumu === 'gonderildi';
                return (
                  <tr key={c.certificate_id} className="border-t border-[var(--border-light)] hover:bg-gray-50 align-top">
                    <td className="p-3"><input type="checkbox" checked={selected.includes(c.certificate_id)} onChange={() => toggle(c.certificate_id)} className="rounded" /></td>
                    <td className="p-3"><div className="font-bold text-xs">{c.student_name}</div><div className="text-[10px] text-[var(--text-muted)]">{c.verification_code}</div></td>
                    <td className="p-3 text-xs">{c.course_title_tr || c.course_title}</td>
                    <td className="p-3">
                      <select value={c.basim_durumu} onChange={(e) => setBasim(c.certificate_id, e.target.value)} className={`${SEL_CLS} ${bm.cls}`}>
                        {BASIM.map((b) => <option key={b.v} value={b.v}>{lang === 'tr' ? b.tr : b.en}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-xs max-w-[180px]">
                      <div className="truncate" title={c.kargo_adresi}>{c.kargo_adresi || <span className="text-red-400">{tr('Adres yok', 'No address')}</span>}</div>
                    </td>
                    <td className="p-3">
                      {shipped ? (
                        <div className="text-xs"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-600">{tr('Gönderildi', 'Shipped')}</span><div className="text-[10px] text-[var(--text-muted)] mt-0.5">{c.takip_no}</div></div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <input value={takip[c.certificate_id] || ''} onChange={(e) => setTakip((p) => ({ ...p, [c.certificate_id]: e.target.value }))} placeholder={tr('Takip no', 'Tracking')} className="w-24 px-2 py-1 rounded border-2 border-[var(--border-light)] text-xs" />
                          <button onClick={() => setKargo(c.certificate_id, 'gonderildi')} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title={tr('Kargoyu gönder', 'Mark shipped')}><Truck size={15} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && certs.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-[var(--text-muted)]">{tr('Sertifika yok', 'No certificates')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-4 border-t border-[var(--border-light)]">
          <span className="text-sm text-[var(--text-muted)] font-semibold">{total}</span>
          <div className="flex gap-2 items-center">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0 || loading} className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"><ChevronLeft size={16} /></button>
            <span className="px-3 py-2 text-sm font-bold">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages || loading} className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
